import React, { useEffect, useState } from 'react';
import {
  Code, Monitor, Server, Smartphone, Database, Cloud, ShieldCheck, Settings,
  Users, Activity, Lock, RefreshCw, Zap, TrendingUp, PieChart, Layers,
  ChevronDown, ChevronUp, CheckCircle2, ArrowRight, ArrowRightCircle,
  Briefcase, HeartPulse, GraduationCap, ShoppingCart, Factory, Building2,
  Building, Plane, Truck, Hotel, Coffee, Target, Rocket
} from 'lucide-react';
import SEO from './SEO';

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border border-white/10 rounded-2xl bg-white/5 overflow-hidden mb-4 transition-all duration-300 hover:border-[#0EA5E9]/30">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 flex items-center justify-between font-semibold text-lg text-left hover:text-[#0EA5E9] transition-colors"
      >
        <span>{question}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-[#0EA5E9]" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      <div className={`px-6 overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-40 pb-5 opacity-100' : 'max-h-0 opacity-0'}`}>
        <p className="text-gray-400 text-lg leading-relaxed">{answer}</p>
      </div>
    </div>
  );
};

const CustomSoftware = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#0A0F1F] min-h-screen text-white pt-24 selection:bg-[#0EA5E9] selection:text-white" style={{ fontFamily: 'emoji' }}>
      <SEO
        title="Custom Software Development in India – Vishwa DigiTech"
        description="Build scalable custom software solutions tailored to your business. Vishwa DigiTech offers ERP, CRM, automation tools, and enterprise software development in India. Free consultation available."
        keywords="custom software development india, ERP development india, CRM software sangli, business automation software, enterprise software development maharashtra, custom IT solutions"
        canonicalPath="/services/software"
      />

      {/* 1. Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute top-0 right-0 w-full h-[800px] bg-gradient-to-br from-[#0EA5E9]/20 via-purple-500/10 to-cyan-500/10 blur-[150px] -z-10" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-600/20 rounded-full blur-[120px] -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-left space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 backdrop-blur-sm mb-2">
                <Code className="w-4 h-4 text-[#0EA5E9]" />
                <span className="text-sm font-bold tracking-widest text-[#0EA5E9] uppercase">Enterprise IT Solutions</span>
              </div>
              <h2 className="text-xl md:text-2xl font-medium text-[#7DD3FC] tracking-wide mb-2">
                Innovate. Automate. Scale.
              </h2>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.1] tracking-tight">
                Custom Software <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Development Solutions</span><br />
                Tailored for You.
              </h1>
              <p className="text-xl text-gray-400 max-w-2xl leading-relaxed">
                We build scalable, secure, and high-performance custom software that automates your business processes and drives unprecedented growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="px-8 py-4 bg-gradient-to-r from-[#0EA5E9] to-blue-600 hover:from-blue-600 hover:to-[#0EA5E9] rounded-xl font-bold text-lg transition-all shadow-[0_0_30px_rgba(14,165,233,0.3)] hover:scale-105 flex items-center justify-center gap-2">
                  Get Free Consultation <ArrowRight className="w-5 h-5" />
                </button>
                <button className="px-8 py-4 border border-white/20 hover:bg-white/10 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2">
                  Request Demo
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-[2rem] blur-xl" />
              <img src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=1000&auto=format&fit=crop" alt="Software Development" className="relative rounded-[2rem] border border-white/10 shadow-2xl object-cover h-[500px] w-full" />
              
              {/* Floating Element */}
              <div className="absolute -bottom-6 -left-6 bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl shadow-xl animate-[float_6s_ease-in-out_infinite]">
                 <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                       <CheckCircle2 className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                       <p className="text-sm text-gray-300">Project Delivery</p>
                       <p className="text-xl font-bold">100% On Time</p>
                    </div>
                 </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. About Custom Software */}
      <section className="py-24 bg-black/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative rounded-[2rem] overflow-hidden border border-white/10 group">
              <div className="absolute inset-0 bg-[#0EA5E9]/20 opacity-0 group-hover:opacity-100 transition-opacity z-10" />
              <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1000&auto=format&fit=crop" alt="Business Growth" className="w-full object-cover group-hover:scale-105 transition-transform duration-700 h-[600px]" />
            </div>
            <div className="space-y-8">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-6 backdrop-blur-md">
                  <Monitor className="w-4 h-4" />
                  The Basics
                </div>
                <h2 className="text-4xl md:text-5xl font-bold">What is <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Custom Software?</span></h2>
              </div>
              <p className="text-xl text-gray-300 leading-relaxed">
                Off-the-shelf software doesn't always fit your unique business processes. Custom software is designed and developed specifically for your organization, addressing your exact needs, workflows, and goals.
              </p>
              <div className="p-6 bg-gradient-to-r from-blue-900/30 to-cyan-900/20 border border-blue-500/30 rounded-2xl backdrop-blur-sm">
                 <p className="text-lg font-medium text-white mb-2">Tailored for Success.</p>
                 <p className="text-gray-400">It provides full ownership, superior security, seamless integration with existing tools, and the flexibility to scale as your business grows.</p>
              </div>
              <ul className="space-y-4 pt-4">
                {[
                  "Built exactly for your workflow",
                  "No recurring licensing fees",
                  "Higher security and compliance",
                  "Scales infinitely with your business"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-4 text-lg text-gray-300">
                    <CheckCircle2 className="w-6 h-6 text-[#0EA5E9] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Why Choose Custom Software */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-6 backdrop-blur-md">
            <TrendingUp className="w-4 h-4" />
            Core Advantages
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Invest in <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Custom Software?</span></h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">Discover the distinct advantages of having a solution built specifically for your enterprise.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {[
            { icon: ShieldCheck, title: "High Security", color: "text-red-400" },
            { icon: TrendingUp, title: "Scalability", color: "text-green-400" },
            { icon: RefreshCw, title: "Automation", color: "text-blue-400" },
            { icon: Cloud, title: "Cloud Ready", color: "text-cyan-400" },
            { icon: Smartphone, title: "Mobile Friendly", color: "text-pink-400" },
            { icon: Layers, title: "Easy Integration", color: "text-purple-400" },
            { icon: Zap, title: "High Performance", color: "text-yellow-400" },
            { icon: PieChart, title: "Business Growth", color: "text-orange-400" },
          ].map((feature, idx) => (
            <div key={idx} className="bg-white/5 border border-white/10 p-5 sm:p-8 rounded-2xl sm:rounded-3xl hover:bg-white/10 hover:-translate-y-2 transition-all duration-300 group backdrop-blur-sm flex flex-col items-center sm:items-start text-center sm:text-left">
              <feature.icon className={`w-8 h-8 sm:w-12 sm:h-12 ${feature.color} mb-3 sm:mb-6 group-hover:scale-110 transition-transform duration-300`} />
              <h3 className="text-sm sm:text-xl font-bold text-white leading-tight">{feature.title}</h3>
            </div>
          ))}
        </div>
      </section>

      {/* 4. Our Software Development Services */}
      <section className="py-24 bg-[#060B14]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-6 backdrop-blur-md">
              <Layers className="w-4 h-4" />
              What We Build
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Software <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Development Services</span></h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">We provide a comprehensive range of software solutions designed to optimize every aspect of your business operations.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: "ERP Software", desc: "Enterprise Resource Planning for total business management.", icon: Building2 },
              { title: "CRM Software", desc: "Customer Relationship Management to boost sales and retention.", icon: Users },
              { title: "School Management", desc: "Complete administration software for educational institutes.", icon: GraduationCap },
              { title: "Hospital Management", desc: "Streamline healthcare operations and patient care.", icon: HeartPulse },
              { title: "Inventory Management", desc: "Track stock, orders, and supply chain efficiently.", icon: Factory },
              { title: "Billing Software", desc: "Automated GST billing, invoicing, and accounting.", icon: PieChart },
              { title: "HRMS Software", desc: "Human Resource and Payroll Management System.", icon: Briefcase },
              { title: "Hotel Management", desc: "Booking, room management, and hospitality operations.", icon: Hotel },
              { title: "Real Estate CRM", desc: "Lead management and property tracking for realtors.", icon: Building },
              { title: "Custom Web Apps", desc: "Tailor-made web applications for specific business needs.", icon: Code },
              { title: "Transport Software", desc: "Fleet management and logistics tracking solutions.", icon: Truck },
              { title: "Mobile Applications", desc: "Native and cross-platform enterprise mobile apps.", icon: Smartphone },
            ].map((service, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-8 rounded-[2rem] hover:border-[#0EA5E9]/50 transition-all duration-300 group">
                <div className="w-14 h-14 bg-[#0EA5E9]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-[#0EA5E9] transition-all">
                  <service.icon className="w-7 h-7 text-[#0EA5E9] group-hover:text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-400 leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Development Process */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#0EA5E9]/10 rounded-full blur-[120px] pointer-events-none -z-10" />
        
        <div className="text-center mb-24">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-6 backdrop-blur-md">
            <Activity className="w-4 h-4" />
            How We Work
          </div>
          <h2 className="text-4xl md:text-5xl font-bold">Our Development <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Process</span></h2>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {/* Central Vertical Line */}
          <div className="hidden md:block absolute left-1/2 top-0 w-1 h-full bg-gradient-to-b from-[#0EA5E9]/0 via-[#0EA5E9]/30 to-[#0EA5E9]/0 -translate-x-1/2"></div>

          <div className="space-y-16 md:space-y-12 relative">
            {[
              { step: "01", title: "Requirement Analysis", desc: "We dive deep into your business needs, goals, and technical requirements to define the exact project scope and objectives.", icon: Target },
              { step: "02", title: "Planning & Strategy", desc: "Creating a comprehensive roadmap, selecting the right technology stack, and defining the scalable system architecture.", icon: PieChart },
              { step: "03", title: "UI/UX Design", desc: "Crafting intuitive, pixel-perfect interfaces that deliver an exceptional, engaging, and seamless user experience.", icon: Layers },
              { step: "04", title: "Agile Development", desc: "Our expert engineers write clean, secure, and scalable code following agile methodologies for rapid delivery.", icon: Code },
              { step: "05", title: "Quality Testing", desc: "Rigorous automated and manual testing ensuring your software is completely bug-free and highly performant.", icon: ShieldCheck },
              { step: "06", title: "Deploy & Support", desc: "Seamless deployment to production environments with continuous monitoring, maintenance, and 24/7 support.", icon: Rocket },
            ].map((item, idx) => (
              <div key={idx} className={`relative flex flex-col md:flex-row items-center gap-8 md:gap-16 ${idx % 2 === 0 ? 'md:flex-row-reverse' : ''}`}>
                
                {/* Timeline Dot */}
                <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-[#0A0F1F] border-4 border-[#0EA5E9] items-center justify-center z-10 shadow-[0_0_20px_rgba(14,165,233,0.5)]">
                  <div className="w-4 h-4 bg-white rounded-full animate-pulse"></div>
                </div>

                {/* Content Card */}
                <div className={`w-full md:w-1/2 flex ${idx % 2 === 0 ? 'md:justify-start' : 'md:justify-end'}`}>
                  <div className="bg-white/[0.02] border border-white/10 p-8 rounded-3xl hover:bg-white/[0.05] hover:border-[#0EA5E9]/50 transition-all duration-500 group relative overflow-hidden w-full max-w-[450px] shadow-xl hover:shadow-[0_0_30px_rgba(14,165,233,0.15)] hover:-translate-y-2 backdrop-blur-sm text-left">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#0EA5E9]/10 rounded-bl-full -z-10 group-hover:bg-[#0EA5E9]/20 transition-colors duration-500" />
                    <div className="absolute -bottom-6 -right-6 text-9xl font-black text-white/[0.02] group-hover:text-white/[0.06] transition-colors duration-500 pointer-events-none">
                      {item.step}
                    </div>
                    
                    <div className="flex items-center gap-6 mb-6">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0EA5E9]/20 to-transparent border border-[#0EA5E9]/30 flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg shrink-0">
                        <item.icon className="w-8 h-8 text-[#0EA5E9]" />
                      </div>
                      <div>
                        <span className="text-[#0EA5E9] font-bold tracking-widest text-sm mb-1 block">STEP {item.step}</span>
                        <h3 className="text-2xl font-bold text-white group-hover:text-[#0EA5E9] transition-colors">{item.title}</h3>
                      </div>
                    </div>
                    <p className="text-gray-400 text-lg leading-relaxed relative z-10">
                      {item.desc}
                    </p>
                  </div>
                </div>

                {/* Empty Space for the other side */}
                <div className="hidden md:block w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Technologies We Use */}
      <section className="py-24 bg-[#050810] border-y border-white/5 relative overflow-hidden">
         {/* Background Effects */}
         <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#0EA5E9]/5 rounded-full blur-[150px] pointer-events-none -z-10" />
         <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[120px] pointer-events-none -z-10" />

         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-6 backdrop-blur-md">
              <Code className="w-4 h-4" />
              Our Tech Stack
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-16">Technologies <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">We Master</span></h2>
            
            <div className="grid md:grid-cols-2 gap-8">
               {[
                  {
                     title: "Frontend & Mobile",
                     icon: Smartphone,
                     techs: ["React.js", "Next.js", "React Native", "Flutter", "Tailwind CSS", "Vue.js"]
                  },
                  {
                     title: "Backend & APIs",
                     icon: Server,
                     techs: ["Node.js", "Python", "PHP", "Laravel", "REST API", "GraphQL"]
                  },
                  {
                     title: "Databases",
                     icon: Database,
                     techs: ["MySQL", "MongoDB", "PostgreSQL", "Firebase", "Redis"]
                  },
                  {
                     title: "Cloud & DevOps",
                     icon: Cloud,
                     techs: ["AWS", "Google Cloud", "Docker", "Kubernetes", "CI/CD Pipeline"]
                  }
               ].map((category, idx) => (
                  <div key={idx} className="bg-white/[0.02] border border-white/10 p-8 md:p-10 rounded-[2rem] hover:bg-white/[0.05] hover:border-[#0EA5E9]/40 transition-all duration-500 group relative overflow-hidden text-left shadow-lg hover:shadow-[0_0_30px_rgba(14,165,233,0.1)] hover:-translate-y-1">
                     <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-[#0EA5E9]/10 to-transparent rounded-bl-full -z-10 group-hover:from-[#0EA5E9]/20 transition-colors duration-700" />
                     
                     <div className="flex items-center gap-5 mb-8">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0EA5E9]/10 to-transparent border border-[#0EA5E9]/20 flex items-center justify-center group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500 shadow-[0_0_15px_rgba(14,165,233,0.1)]">
                           <category.icon className="w-8 h-8 text-[#0EA5E9]" />
                        </div>
                        <h3 className="text-2xl font-bold text-white group-hover:text-[#0EA5E9] transition-colors duration-300">{category.title}</h3>
                     </div>
                     
                     <div className="flex flex-wrap gap-3">
                        {category.techs.map((tech, i) => (
                           <span key={i} className="px-4 py-2.5 bg-[#050810]/50 border border-white/5 rounded-xl text-gray-300 font-medium group-hover:border-[#0EA5E9]/30 hover:bg-[#0EA5E9]/10 hover:text-white transition-all duration-300 flex items-center gap-2 cursor-default">
                              <div className="w-1.5 h-1.5 rounded-full bg-[#0EA5E9]/50 group-hover:bg-[#0EA5E9] group-hover:animate-pulse transition-colors" />
                              {tech}
                           </span>
                        ))}
                     </div>
                  </div>
               ))}
            </div>
         </div>
      </section>

      {/* 7. Industries We Serve & 8. Key Features (Combined layout) */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
         <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            
            {/* Industries */}
            <div className="lg:sticky lg:top-32 relative z-10">
               <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-6 backdrop-blur-md">
                 <Users className="w-4 h-4" />
                 Who We Help
               </div>
               <h2 className="text-4xl font-bold mb-10">Industries <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">We Serve</span></h2>
               <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {[
                     { name: "Healthcare", icon: HeartPulse },
                     { name: "Education", icon: GraduationCap },
                     { name: "Retail & Ecommerce", icon: ShoppingCart },
                     { name: "Manufacturing", icon: Factory },
                     { name: "Finance", icon: PieChart },
                     { name: "Hospitality", icon: Hotel },
                     { name: "Logistics", icon: Truck },
                     { name: "Real Estate", icon: Building },
                  ].map((ind, idx) => (
                     <div key={idx} className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-white/5 border border-white/10 rounded-2xl hover:border-[#0EA5E9]/40 transition-colors">
                        <ind.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#0EA5E9] shrink-0" />
                        <span className="font-semibold text-xs sm:text-base leading-tight">{ind.name}</span>
                     </div>
                  ))}
               </div>
            </div>

            {/* Key Features */}
            <div className="mt-8 lg:mt-0 relative z-0">
               <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-6 backdrop-blur-md">
                 <ShieldCheck className="w-4 h-4" />
                 What You Get
               </div>
               <h2 className="text-4xl font-bold mb-10">Key <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Features</span></h2>
               <div className="space-y-4 max-h-none lg:max-h-[500px] overflow-y-visible lg:overflow-y-auto pr-2 hide-scrollbar">
                  {[
                     "Customized Dashboards & Reporting",
                     "Role-based Access Control (RBAC)",
                     "Secure Data Encryption",
                     "Third-party API Integrations",
                     "Real-time Analytics",
                     "Automated Workflows & Alerts",
                     "Cloud Hosting & Backup",
                     "24/7 Technical Support"
                  ].map((feature, idx) => (
                     <div key={idx} className="flex items-center gap-4 p-4 sm:p-5 bg-gradient-to-r from-white/5 to-transparent border-l-4 border-[#0EA5E9] rounded-r-xl">
                        <CheckCircle2 className="w-6 h-6 text-[#0EA5E9] shrink-0" />
                        <span className="text-base sm:text-lg font-medium">{feature}</span>
                     </div>
                  ))}
               </div>
            </div>

         </div>
      </section>

      {/* 9. Business Benefits */}
      <section className="py-24 bg-[#060B14] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-6 backdrop-blur-md">
              <Zap className="w-4 h-4" />
              The Value
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Business <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Benefits</span></h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">See how our custom software solutions transform your organization.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
             {[
                { title: "Increase Productivity", desc: "Automate manual tasks and save hundreds of hours.", icon: Zap },
                { title: "Reduce Costs", desc: "Eliminate recurring license fees for multiple tools.", icon: TrendingUp },
                { title: "Secure Data", desc: "Keep your business data safe with custom security.", icon: Lock },
                { title: "Better Customer Exp", desc: "Provide a seamless experience for your clients.", icon: HeartPulse },
             ].map((benefit, idx) => (
                <div key={idx} className="p-8 bg-white/5 border border-white/10 rounded-3xl hover:bg-white/10 transition-colors">
                   <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-6">
                      <benefit.icon className="w-6 h-6 text-purple-400" />
                   </div>
                   <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                   <p className="text-gray-400 leading-relaxed">{benefit.desc}</p>
                </div>
             ))}
          </div>
        </div>
      </section>

      {/* 10. Why Choose Vishwa DigiTech */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
         <div className="bg-gradient-to-br from-[#0EA5E9]/10 to-purple-600/10 border border-white/10 rounded-[3rem] p-10 md:p-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#0EA5E9]/20 blur-[100px] rounded-full mix-blend-screen" />
            
            <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
               <div>
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-6 backdrop-blur-md">
                    <Briefcase className="w-4 h-4" />
                    Why Choose Us
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold mb-6">Partner with <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Vishwa DigiTech</span></h2>
                  <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                     With years of experience in enterprise software development, we don't just write code; we solve business problems. Our team of expert developers and analysts ensure your software is future-proof.
                  </p>
                  <div className="grid grid-cols-2 gap-6">
                     <div>
                        <h4 className="text-4xl font-black text-white mb-2">100+</h4>
                        <p className="text-gray-400">Projects Delivered</p>
                     </div>
                     <div>
                        <h4 className="text-4xl font-black text-white mb-2">50+</h4>
                        <p className="text-gray-400">Enterprise Clients</p>
                     </div>
                     <div>
                        <h4 className="text-4xl font-black text-white mb-2">99%</h4>
                        <p className="text-gray-400">Client Satisfaction</p>
                     </div>
                     <div>
                        <h4 className="text-4xl font-black text-white mb-2">24/7</h4>
                        <p className="text-gray-400">Technical Support</p>
                     </div>
                  </div>
               </div>
               <div className="relative">
                  <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1000&auto=format&fit=crop" alt="Team" className="rounded-[2rem] border border-white/20 shadow-2xl" />
               </div>
            </div>
         </div>
      </section>

      {/* 11. FAQ Section */}
      <section className="py-24 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-6 backdrop-blur-md">
            <CheckCircle2 className="w-4 h-4" />
            Got Questions?
          </div>
          <h2 className="text-4xl font-bold mb-4">Frequently Asked <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Questions</span></h2>
          <p className="text-gray-400 text-xl">Find answers to common questions about our custom software development services.</p>
        </div>

        <div className="space-y-4">
          {[
            {
              q: "How much does custom software development cost?",
              a: "The cost varies depending on the complexity, features, and platform requirements. We provide a detailed proposal and cost estimate after understanding your exact business needs during our free consultation."
            },
            {
              q: "How long does it take to build custom software?",
              a: "Development time depends on the project scope. A basic MVP can take 4-8 weeks, while complex enterprise solutions can take several months. We follow agile methodology to ensure rapid delivery of core features."
            },
            {
              q: "Will I own the source code?",
              a: "Yes! Once the project is completed and fully paid for, you will have 100% ownership of the custom software and its source code."
            },
            {
              q: "Do you provide support and maintenance after launch?",
              a: "Absolutely. We offer various support and maintenance packages to ensure your software remains secure, up-to-date, and optimized for performance as your business grows."
            },
            {
              q: "Can the new software integrate with our existing tools?",
              a: "Yes, we can build custom APIs and integrations to ensure your new software communicates seamlessly with your existing tools like accounting software, CRM, or payment gateways."
            }
          ].map((faq, idx) => (
            <FAQItem key={idx} question={faq.q} answer={faq.a} />
          ))}
        </div>
      </section>

     

    </div>
  );
};

export default CustomSoftware;
